// frontend/src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ClerkProvider } from '@clerk/clerk-react';
import { AuthProvider } from './context/AuthContext';
import theme from './theme';

// Components
import Layout from './components/layout/Layout';
import ProtectedRoute from './routes/ProtectedRoute';
import RequirePermission from './routes/RequirePermission';
import LandingGate from './routes/LandingGate';
import { landingFor } from './routes/landing';
import { NotFound } from './components/layout/SystemState';
import MyQueue from './components/adjudication/MyQueue';
import AdjudicationItem from './components/adjudication/AdjudicationItem';
import LessonSheet from './components/annotation/LessonSheet';
import LessonLibrary from './components/research/LessonLibrary';
import LessonDetail from './components/research/LessonDetail';
import Agreement from './components/research/Agreement';
import RunsList from './components/runs/RunsList';
import ImportWizard from './components/runs/ImportWizard';
import RunSetup from './components/runs/RunSetup';
import AdasRunWizard from './components/runs/AdasRunWizard';
import RunSummary from './components/runs/RunSummary';
import UserProfile, { AccountDeleted } from './components/profile/UserProfile';
import { SignInPage, SignUpPage, InvitationUnusable } from './components/auth/AuthPages';
import { clerkAppearance, clerkLocalization } from './components/auth/clerkTheme';
import MyLessons from './components/teacher/MyLessons';
import UploadLesson from './components/teacher/UploadLesson';
import LessonReport from './components/teacher/LessonReport';
import UserManagement from './components/admin/UserManagement';
import Activity from './components/admin/Activity';
import ActivityPerson from './components/admin/ActivityPerson';
import { useAuth } from './context/AuthContext';

// Each role's landing page, first match (routes/landing.js, call-map §2)
const RootRedirect = () => {
    const { can } = useAuth();
    return <Navigate to={landingFor(can).path} replace />;
};

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
    throw new Error('Missing Publishable Key');
}

// Clerk moves between its own steps (/login/factor-one, /register/verify-email-address) through
// these; without them it sets window.location and the whole app reloads. BrowserRouter listens
// for popstate, so it picks up the new URL without a reload.
const clerkNavigate = (replace) => (to) => {
    window.history[replace ? 'replaceState' : 'pushState']({}, '', to);
    window.dispatchEvent(new PopStateEvent('popstate'));
};

function App() {
    return (
        <ClerkProvider
            routerPush={clerkNavigate(false)}
            routerReplace={clerkNavigate(true)}
            publishableKey={clerkPubKey}
            signInUrl="/login"
            signUpUrl="/register"
            signInFallbackRedirectUrl="/"
            signUpFallbackRedirectUrl="/"
            afterSignOutUrl="/login"
            appearance={clerkAppearance}
            localization={clerkLocalization}
        >
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AuthProvider>
                        <BrowserRouter>
                            {/* Signed-out visitors on "/" see the public landing page (YES-276) */}
                            <LandingGate>
                            <Layout>
                                <Routes>
                                    {/* Public Routes */}
                                    <Route path="/login/*" element={<SignInPage />} />
                                    <Route path="/register/*" element={<SignUpPage />} />
                                    <Route path="/invitation-unusable" element={<InvitationUnusable />} />
                                    <Route path="/account-deleted" element={<AccountDeleted />} />

                                    {/* Protected Routes */}
                                    <Route element={<ProtectedRoute />}>
                                        <Route path="/" element={<RootRedirect />} />
                                        {/* Teacher: My lessons, upload, and a lesson's analysis (YES-264–266); /analysis/:sessionId retired */}
                                        <Route path="/dashboard" element={<RequirePermission permission="lesson.own"><MyLessons /></RequirePermission>} />
                                        <Route path="/upload" element={<RequirePermission permission="lesson.upload"><UploadLesson /></RequirePermission>} />
                                        <Route path="/lessons/:lessonId" element={<RequirePermission permission="lesson.own"><LessonReport /></RequirePermission>} />
                                        <Route path="/profile" element={<UserProfile />} />

                                        {/* My queue and the adjudication item (YES-255–258): the URL names the run, never an item */}
                                        <Route path="/adjudicate" element={<RequirePermission permission="coding.assigned"><MyQueue /></RequirePermission>} />
                                        <Route path="/adjudicate/:runId" element={<RequirePermission permission="coding.assigned"><AdjudicationItem /></RequirePermission>} />
                                        <Route path="/annotate/:assignmentId" element={<RequirePermission permission="coding.assigned"><LessonSheet /></RequirePermission>} />

                                        {/* Researcher's Lessons and lesson detail (YES-272); /lessons/:lessonId stays the teacher's analysis */}
                                        <Route path="/lessons" element={<RequirePermission permission="lesson.research"><LessonLibrary /></RequirePermission>} />
                                        <Route path="/research/lessons/:lessonId" element={<RequirePermission permission="lesson.research"><LessonDetail /></RequirePermission>} />

                                        {/* Runs, import and lesson assignment (YES-259, YES-260): researchers and admins */}
                                        <Route path="/runs" element={<RequirePermission permission="run.manage"><RunsList /></RequirePermission>} />
                                        {/* Linked from Runs; needs run.manage and lesson.research (YES-269) */}
                                        <Route path="/runs/agreement" element={<RequirePermission permission="lesson.research"><RequirePermission permission="run.manage"><Agreement /></RequirePermission></RequirePermission>} />
                                        <Route path="/runs/import" element={<RequirePermission permission="run.manage"><ImportWizard /></RequirePermission>} />
                                        <Route path="/runs/:runId" element={<RequirePermission permission="run.manage"><RunSummary /></RequirePermission>} />
                                        <Route path="/runs/:runId/setup" element={<RequirePermission permission="run.manage"><RunSetup /></RequirePermission>} />
                                        {/* New run from lessons and Add lessons (YES-267): candidate lessons need lesson.research too */}
                                        <Route path="/runs/new" element={<RequirePermission permission="lesson.research"><RequirePermission permission="run.manage"><AdasRunWizard mode="new" /></RequirePermission></RequirePermission>} />
                                        <Route path="/runs/:runId/add" element={<RequirePermission permission="lesson.research"><RequirePermission permission="run.manage"><AdasRunWizard mode="add" /></RequirePermission></RequirePermission>} />

                                        {/* Admin: /admin has no page of its own (wf-admin-root-1); Activity: wf-admin-activity, YES-316 */}
                                        <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
                                        <Route path="/admin/users" element={<RequirePermission permission="users.manage"><UserManagement /></RequirePermission>} />
                                        <Route path="/admin/activity" element={<RequirePermission permission="audit.read"><Activity /></RequirePermission>} />
                                        <Route path="/admin/activity/:userId" element={<RequirePermission permission="audit.read"><ActivityPerson /></RequirePermission>} />

                                        {/* Unknown route: page not found, never a silent redirect (wf-shell-state-6) */}
                                        <Route path="*" element={<NotFound />} />
                                    </Route>
                                </Routes>
                            </Layout>
                            </LandingGate>
                        </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </ClerkProvider>
    );
}

export default App;
